// Shared table-cell looks for the lead pages (presentation only).
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { QR } from "../../qrCode/components/qrUi";

const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#d97706", "#db2777", "#16a34a", "#4f46e5"];

const colorFor = (text = "") => {
    let h = 0;
    for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

// Contact / employee name with an initial avatar. Clickable when onClick is passed.
export function NameCell({ name, sub, onClick, title }) {
    const c = colorFor(name || "");
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.1}
            title={title}
            onClick={onClick}
            sx={{
                minWidth: 0,
                cursor: onClick ? "pointer" : "default",
                ...(onClick ? { // clickable: always blue + underlined (darker on hover)
                        "& .lead-name": { color: "#2563eb !important", textDecoration: "underline", textDecorationColor: "#2563eb66", textUnderlineOffset: "3px" },
                        "&:hover .lead-name": { color: "#1d4ed8 !important", textDecorationColor: "#1d4ed8" } } : {}),
            }}
        >
            <Avatar sx={{ width: 28, height: 28, fontSize: 12.5, fontWeight: 700, color: "#fff", bgcolor: c }}>
                {(name || "?").trim().charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
                <Typography className="lead-name" variant="body2" fontWeight={700} color="#0f172a" noWrap>
                    {name || "-"}
                </Typography>
                {sub && (
                    <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ lineHeight: 1.2 }}>
                        {sub}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}

// Comma separated values ("a, b, c") as small chips; shows "+n" after `max`.
export function ChipList({ value, color = QR.purple, max = 2 }) {
    const items = String(value || "").split(",").map((x) => x.trim()).filter(Boolean);
    if (!items.length) return <Typography variant="body2" color="#cbd5e1">-</Typography>;
    const shown = items.slice(0, max);
    const rest = items.length - shown.length;
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" title={items.join(", ")} sx={{ flexWrap: "nowrap" }}>
            {shown.map((t) => (
                <Box key={t} component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 5, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", color, bgcolor: `${color}12`, border: `1px solid ${color}26` }}>
                    {t}
                </Box>
            ))}
            {rest > 0 && (
                <Box component="span" sx={{ px: 0.75, py: 0.2, borderRadius: 5, fontSize: 11.5, fontWeight: 700, color: "#64748b", bgcolor: "#f1f5f9" }}>
                    +{rest}
                </Box>
            )}
        </Stack>
    );
}

// Assigned person (or a grey "Unassigned")
export function PersonChip({ name }) {
    if (!name) {
        return (
            <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 5, fontSize: 11.5, fontWeight: 700, color: "#94a3b8", bgcolor: "#f1f5f9", border: "1px dashed #cbd5e1" }}>
                Unassigned
            </Box>
        );
    }
    return (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: 11, fontWeight: 700, bgcolor: colorFor(name) }}>{name.charAt(0).toUpperCase()}</Avatar>
            <Typography variant="body2" noWrap color="#334155">{name}</Typography>
        </Stack>
    );
}

// Colour for a lead status name (negatives first, so "Not Interested" is red, not green)
export function statusColor(value) {
    const v = String(value || "").toLowerCase();
    if (/(total|all)/.test(v)) return QR.blue;
    return (
        /\bwon\b/.test(v) ? "#16a34a" :
            /(lost|not |not-|reject|cancel|drop|dead|closed|wrong|invalid)/.test(v) ? "#dc2626" :
                /(convert|confirm|success|done|complete|interest|admit|join|enrol)/.test(v) ? "#16a34a" :
                    /(follow|call|pending|later|callback|hold)/.test(v) ? "#d97706" :
                        /(new|fresh|open)/.test(v) ? QR.blue : "#7c3aed");
}

// Lead status pill
export function StatusPill({ value }) {
    if (!value) return <Typography variant="body2" color="#cbd5e1">-</Typography>;
    const c = statusColor(value);
    return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1, py: 0.3, borderRadius: 5, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", color: c, bgcolor: `${c}14`, border: `1px solid ${c}33` }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c }} />
            {value}
        </Box>
    );
}

// Muted plain text cell
export function MutedText({ value }) {
    return (
        <Typography variant="body2" color={value ? "text.secondary" : "#cbd5e1"} noWrap title={value || ""} sx={{ maxWidth: 220 }}>
            {value || "-"}
        </Typography>
    );
}

// Filter box width in the table toolbar
export const filterBoxSx = { width: { xs: "100%", sm: 230 } };
